import { create } from "zustand";
import { persist } from "zustand/middleware";

// Import groupCartItems for deal pricing calculation
// Note: This is a circular dependency, so we'll inline the logic instead

// ============================================================================
// Cart Store
// Manages shopping cart state with sessionStorage persistence
// ============================================================================

export interface CartItem {
  id: string;
  name: string;
  description: string;
  productDescription?: string;
  price: string | number;
  image: string;
  quantity: number;
  nutritionalIcons: string[];
  customizations?: {
    size?: string;
    crust?: string;
    toppings?: string[];
    extras?: string[];
    addons?: string[];
    // Structured added/removed lists vs the default recipe — populated for
    // deal-step pizzas so the basket DealGroupCard and the order
    // confirmation surfaces can render green "+ added" and red "- removed"
    // lines (mirrors the standalone pizza display).
    addedNames?: string[];
    removedNames?: string[];
  };
  dealGroupId?: string;
  dealId?: number;
  dealName?: string;
  dealPrice?: number;
  /** Raw deal.price at add-time (no customization surcharge) — used for price-change validation. */
  dealBasePrice?: number;
  /** Base price of this step's selected item at selected size — used to recompute % discount on edit. */
  selectedItemBasePrice?: number;
  /** Size variant ID selected by the user — used for size-specific price validation on checkout. */
  sizeId?: number;
  /** Base size price at add-time (no crust/topping surcharges) — used for price-change validation. */
  basePrice?: number;
  categoryId?: number;
  category?: string;
  isPremium?: boolean;
  menuItemId?: number;
  allergens?: string[];
  calories?: number;
  serves?: string;
  defaultIngredients?: {
    base?: number[];
    sauce?: number[];
    cheese?: number[];
    topping?: number[];
  };
  selectedIngredients?: {
    base?: number[];
    sauce?: number[];
    cheese?: number[];
    topping?: number[];
  };
  /**
   * Ingredient ids the customer set to EXTRA (double portion) on a standalone
   * pizza, including locked (mandatory+extra) base-recipe items.
   *
   * `customizations.extras` holds display NAMES with locked items filtered out,
   * so it cannot rebuild the customizer state on edit. Without these ids the
   * basket → Edit round-trip re-opened the pizza with every extra downgraded to
   * a single portion, and pressing Update silently saved the cheaper recipe.
   */
  extraIngredientIds?: number[];
  /**
   * Base-recipe topping ids the customer removed at any point while
   * customising (kept even if re-added). Pricing treats a re-added base topping
   * as an ADD, so without this history basket → Edit → Update re-priced the
   * same pizza lower.
   */
  everRemovedIngredientIds?: number[];
  ingredientNamesMap?: Record<number, string>;
  ingredientAllergens?: Array<{ ingredientName: string; allergens: string[] }>;
  /** Per-step customisation surcharge (premium crust, extra toppings) — preserved for BOGO re-pricing on edit. */
  customizationCharge?: number;
  /**
   * Menu-item ids of addon products bundled into this line (e.g. the dips chosen
   * for a Chicken & Dips combo). These are real products, not ingredients, so
   * checkout validation must check their availability like any other product —
   * an unpublished dip should not be orderable.
   */
  addonProductIds?: number[];
  /** Deal-step customization IDs preserved for editing a deal item from the basket. */
  dealCustomization?: {
    sizeId?: number | null;
    crustId?: number | null;
    toppingIds?: number[];
    extraToppingIds?: number[];
    /** Base toppings removed at any point while customising this step. */
    everRemovedToppingIds?: number[];
  };
}

export function getItemNumericPrice(item: CartItem): number {
  if (typeof item.price === "number") {
    return isNaN(item.price) ? 0 : item.price;
  }
  if (typeof item.price === "string") {
    // Remove all currency symbols and parse
    const cleaned = item.price.replace(/[£€$]/g, "").trim();
    const parsed = parseFloat(cleaned);
    return isNaN(parsed) ? 0 : parsed;
  }
  return 0;
}

interface CartState {
  cartItems: CartItem[];
}

interface CartActions {
  addToCart: (item: Omit<CartItem, "quantity">, quantity?: number) => void;
  removeFromCart: (id: string) => void;
  updateQuantity: (id: string, quantity: number) => void;
  clearCart: () => void;
}

type CartStore = CartState & CartActions;

const initialState: CartState = {
  cartItems: [],
};

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      addToCart: (item, quantity = 1) => {
        set((state) => {
          const existingItem = state.cartItems.find(
            (cartItem) => cartItem.id === item.id,
          );

          if (existingItem) {
            return {
              cartItems: state.cartItems.map((cartItem) =>
                cartItem.id === item.id
                  ? { ...cartItem, quantity: cartItem.quantity + quantity }
                  : cartItem,
              ),
            };
          } else {
            return {
              cartItems: [...state.cartItems, { ...item, quantity }],
            };
          }
        });
      },

      removeFromCart: (id) => {
        set((state) => ({
          cartItems: state.cartItems.filter((item) => item.id !== id),
        }));
      },

      updateQuantity: (id, quantity) => {
        if (quantity <= 0) {
          get().removeFromCart(id);
          return;
        }

        set((state) => ({
          cartItems: state.cartItems.map((item) =>
            item.id === id ? { ...item, quantity } : item,
          ),
        }));
      },

      clearCart: () => {
        set(initialState);
      },
    }),
    {
      name: "cart-storage",
      storage: {
        getItem: (name) => {
          // Try sessionStorage first, then migrate from old localStorage
          let str = sessionStorage.getItem(name);
          if (!str) {
            try {
              const oldData = localStorage.getItem(name);
              if (oldData) {
                sessionStorage.setItem(name, oldData);
                localStorage.removeItem(name);
                str = oldData;
              }
            } catch {
              /* ignore */
            }
          }
          return str ? JSON.parse(str) : null;
        },
        setItem: (name, value) => {
          sessionStorage.setItem(name, JSON.stringify(value));
        },
        removeItem: (name) => {
          sessionStorage.removeItem(name);
        },
      },
    },
  ),
);

// ============================================================================
// Computed Selectors (pure functions for optimized re-renders)
// ============================================================================

export const selectCartItems = (state: CartStore) => state.cartItems;

export const selectCartCount = (state: CartStore) =>
  state.cartItems.reduce((total, item) => total + item.quantity, 0);

export const selectCartSubtotal = (state: CartStore) =>
  state.cartItems.reduce((total, item) => {
    // For deal items, don't sum individual prices - deals are priced as a unit
    if (item.dealGroupId) {
      return total; // Deal items contribute 0 to subtotal (deal price added separately)
    }
    const price = getItemNumericPrice(item);
    return total + price * item.quantity;
  }, 0);

export const selectCartTotal = (state: CartStore) => {
  // Inline deal grouping logic to avoid circular dependency
  const deals = new Map<string, { dealName: string; dealPrice: number; items: CartItem[] }>();

  for (const item of state.cartItems) {
    if (item.dealGroupId) {
      const existing = deals.get(item.dealGroupId);
      if (existing) {
        existing.items.push(item);
      } else {
        deals.set(item.dealGroupId, {
          dealName: item.dealName || "Deal",
          dealPrice: item.dealPrice || getItemNumericPrice(item),
          items: [item],
        });
      }
    }
  }

  // Start with standalone items subtotal
  let total = selectCartSubtotal(state);

  // Add deal prices (each deal priced as a single unit)
  for (const deal of deals.values()) {
    total += deal.dealPrice;
  }

  return total;
};
